import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    styled,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import DisplayOrderModal from 'components/products/DisplayOrderModal';
import { Page, ControlBar, ControlBarHeader } from 'components/ui/layout';
import { LoadingPage, ErrorPage } from 'components/ui/states';
import ConfirmAction from 'components/popups/ConfirmAction';
import { CancelButton, SubmitButton } from 'components/ui/buttons';
import { useEvent } from 'context/event';
import { useNotification } from 'context/notification';
import {
    createOnsiteRole,
    deleteOnsiteRole,
    getOnsiteRoleProducts,
    getOnsiteRoles,
    updateOnsiteRole,
    updateOnsiteRoleSort,
} from './OnsiteRolesAPI';

export function buildRolePayload(name, productSettings) {
    return {
        name: name.trim(),
        products: productSettings
            .filter(
                (product) =>
                    !product.scannerAllowed || (!product.scannerOnly && !product.posAllowed),
            )
            .map(({ productId, scannerAllowed, posAllowed, scannerOnly }) => ({
                productId,
                scannerAllowed,
                posAllowed: scannerOnly ? false : posAllowed,
            })),
    };
}

export function getDevicePillColor(enabledCount, totalCount) {
    if (totalCount === 0 || enabledCount === 0) return 'gray';
    if (enabledCount === totalCount) return 'green';
    return 'gold';
}

function roleSettings(role, products) {
    const rules = new Map(role?.products?.map((rule) => [rule.productId, rule]) || []);

    return products.map((product) => ({
        productId: product.product_id,
        name: product.prod_name,
        isSeason: Boolean(product.is_season),
        scannerOnly: Boolean(product.scanner_only),
        posAvailable: Boolean(product.pos_visible),
        scannerAllowed: rules.get(product.product_id)?.scannerAllowed ?? true,
        posAllowed: product.scanner_only
            ? false
            : (rules.get(product.product_id)?.posAllowed ?? true),
    }));
}

export default function OnsiteRoles(props) {
    const eventId = Number(props.match.params.id);
    const event = useEvent();
    const { notify } = useNotification();
    const [status, setStatus] = useState('loading');
    const [roles, setRoles] = useState([]);
    const [products, setProducts] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [editor, setEditor] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [menu, setMenu] = useState({ anchorEl: null, role: null });
    const [reorderOpen, setReorderOpen] = useState(false);
    const [reorderLoading, setReorderLoading] = useState(false);

    useEffect(() => {
        setStatus('loading');
        Promise.all([getOnsiteRoles(eventId), getOnsiteRoleProducts(eventId)])
            .then(([roleList, productList]) => {
                setRoles(roleList);
                setProducts(
                    productList.map((product) => ({ ...product, time_zone: event.time_zone })),
                );
                setStatus('success');
            })
            .catch(() => setStatus('error'));
    }, [eventId, event.time_zone, refresh]);

    const saveRole = async (roleId, payload) => {
        try {
            if (roleId) {
                await updateOnsiteRole(eventId, roleId, payload);
                notify.success('Onsite role updated');
            } else {
                await createOnsiteRole(eventId, payload);
                notify.success('Onsite role created');
            }
            setEditor(null);
            setRefresh((value) => value + 1);
        } catch (error) {
            throw error;
        }
    };

    const confirmDelete = async () => {
        try {
            await deleteOnsiteRole(eventId, deleting.roleId);
            notify.success('Onsite role deleted');
            setDeleting(null);
            setRefresh((value) => value + 1);
        } catch (error) {
            notify.error(error.message);
        }
    };

    const closeMenu = () => setMenu({ anchorEl: null, role: null });

    const editMenuRole = () => {
        setEditor({ role: menu.role });
        closeMenu();
    };

    const deleteMenuRole = () => {
        setDeleting(menu.role);
        closeMenu();
    };

    const saveRoleOrder = async (sort) => {
        setReorderLoading(true);
        try {
            await updateOnsiteRoleSort(eventId, sort);
            setReorderOpen(false);
            setRefresh((value) => value + 1);
            notify.success('Saved Order');
        } catch (error) {
            notify.error('Unable to change order');
        } finally {
            setReorderLoading(false);
        }
    };

    if (status === 'loading') return <LoadingPage />;
    if (status === 'error') return <ErrorPage message="Unable to load onsite roles" />;

    return (
        <Page>
            <ControlBar>
                <ControlBarHeader>Onsite Roles</ControlBarHeader>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    {roles.length > 1 && (
                        <Button
                            variant="outlined"
                            startIcon={<SwapVertIcon />}
                            onClick={() => setReorderOpen(true)}
                        >
                            Set Display Order
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => setEditor({ role: null })}
                    >
                        Create Role
                    </Button>
                </Box>
            </ControlBar>

            <Alert severity="info" sx={{ mb: 3 }}>
                Create roles for your scanner and pos users to login with. These roles control which
                tickets can be scanned or sold. (Note: users can still choose 'no profile' to avoid
                any restrictions if needed)
            </Alert>

            {roles.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6">No onsite roles yet</Typography>
                    <Typography color="text.secondary">
                        Create a role to manage which tickets a Scanner or POS device can process.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table aria-label="Onsite roles">
                        <TableHead>
                            <TableRow>
                                <TableCell>Role</TableCell>
                                <TableCell>Scanner</TableCell>
                                <TableCell>POS</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {roles.map((role) => {
                                const settings = roleSettings(role, products);
                                const totalTickets = settings.length;
                                const totalPosTickets = settings.filter(
                                    (product) => !product.scannerOnly,
                                ).length;
                                const scannerEnabled = settings.filter(
                                    (product) => product.scannerAllowed,
                                ).length;
                                const posEnabled = settings.filter(
                                    (product) => product.posAllowed,
                                ).length;

                                return (
                                    <TableRow key={role.roleId}>
                                        <TableCell>
                                            <TitleText onClick={() => setEditor({ role })}>
                                                {role.name}
                                            </TitleText>
                                        </TableCell>
                                        <TableCell>
                                            <MetaPill
                                                pillcolor={getDevicePillColor(
                                                    scannerEnabled,
                                                    totalTickets,
                                                )}
                                                label={`${scannerEnabled}/${totalTickets} enabled`}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <MetaPill
                                                pillcolor={getDevicePillColor(
                                                    posEnabled,
                                                    totalPosTickets,
                                                )}
                                                label={`${posEnabled}/${totalPosTickets} enabled`}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <IconButton
                                                aria-label={`Actions for ${role.name}`}
                                                size="small"
                                                onClick={(event) =>
                                                    setMenu({
                                                        anchorEl: event.currentTarget,
                                                        role,
                                                    })
                                                }
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Menu anchorEl={menu.anchorEl} open={Boolean(menu.anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={editMenuRole}>Edit</MenuItem>
                <MenuItem onClick={deleteMenuRole} sx={{ color: 'error.main' }}>
                    Delete
                </MenuItem>
            </Menu>

            <RoleEditor
                open={Boolean(editor)}
                role={editor?.role}
                products={products}
                onClose={() => setEditor(null)}
                onSave={saveRole}
            />
            <DisplayOrderModal
                open={reorderOpen}
                items={roles}
                getItemId={(role) => role.roleId}
                getItemLabel={(role) => role.name}
                description="Drag and drop roles to change their display order."
                onClose={() => !reorderLoading && setReorderOpen(false)}
                onSave={saveRoleOrder}
                loading={reorderLoading}
            />
            <ConfirmAction
                open={Boolean(deleting)}
                destructive
                confirmText="Delete"
                title="Delete onsite role?"
                description="Devices already using this role retain their cached settings until they leave the event or change roles."
                onCancel={() => setDeleting(null)}
                onConfirm={confirmDelete}
            />
        </Page>
    );
}

export function RoleEditor({ open, role, products, onClose, onSave }) {
    const [name, setName] = useState('');
    const [settings, setSettings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setName(role?.name || '');
        setSettings(roleSettings(role, products));
        setSaving(false);
        setError('');
    }, [open, role, products]);

    const updatePermission = (productId, permission, value) => {
        setSettings((current) =>
            current.map((product) =>
                product.productId === productId
                    ? {
                          ...product,
                          [permission]:
                              permission === 'posAllowed' && product.scannerOnly ? false : value,
                      }
                    : product,
            ),
        );
    };

    const updateAllPermissions = (permission, value) => {
        setSettings((current) =>
            current.map((product) => ({
                ...product,
                [permission]: permission === 'posAllowed' && product.scannerOnly ? false : value,
            })),
        );
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Enter a role name');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await onSave(role?.roleId, buildRolePayload(name, settings));
        } catch (saveError) {
            setError(saveError.message);
            setSaving(false);
        }
    };

    const scannerAllowedCount = settings.filter((product) => product.scannerAllowed).length;
    const posAllowedCount = settings.filter((product) => product.posAllowed).length;
    const posProductCount = settings.filter((product) => !product.scannerOnly).length;
    const noTicketsEnabled = scannerAllowedCount === 0 && posAllowedCount === 0;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>{role ? 'Edit Onsite Role' : 'Create Onsite Role'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        autoFocus={role ? undefined : true}
                        label="Role name"
                        value={name}
                        inputProps={{ maxLength: 120 }}
                        onChange={(event) => setName(event.target.value)}
                    />
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Product permissions
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            Products are allowed by default. Season tickets from the parent event
                            can only be enabled for scanning.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <MetaPill
                                pillcolor={getDevicePillColor(scannerAllowedCount, settings.length)}
                                label={`${scannerAllowedCount}/${settings.length} Scanner`}
                            />
                            <MetaPill
                                pillcolor={getDevicePillColor(posAllowedCount, posProductCount)}
                                label={`${posAllowedCount}/${posProductCount} POS`}
                            />
                        </Stack>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                                alignItems: 'center',
                                columnGap: 2,
                                p: 2,
                            }}
                        >
                            <Box />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            settings.length > 0 &&
                                            scannerAllowedCount === settings.length
                                        }
                                        indeterminate={
                                            scannerAllowedCount > 0 &&
                                            scannerAllowedCount < settings.length
                                        }
                                        onChange={(event) =>
                                            updateAllPermissions(
                                                'scannerAllowed',
                                                event.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="Scanner"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={posProductCount === 0}
                                        checked={
                                            posProductCount > 0 &&
                                            posAllowedCount === posProductCount
                                        }
                                        indeterminate={
                                            posAllowedCount > 0 && posAllowedCount < posProductCount
                                        }
                                        onChange={(event) =>
                                            updateAllPermissions('posAllowed', event.target.checked)
                                        }
                                    />
                                }
                                label="POS"
                            />
                        </Box>
                        <Stack spacing={1}>
                            {settings.map((product) => (
                                <Paper key={product.productId} variant="outlined" sx={{ p: 2 }}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                                            alignItems: 'center',
                                            columnGap: 2,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography>{product.name}</Typography>
                                                {product.isSeason && (
                                                    <Chip label="Season" size="small" />
                                                )}
                                            </Stack>
                                            {product.scannerOnly ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Available for scanning only.
                                                </Typography>
                                            ) : !product.posAvailable ? (
                                                <Typography variant="caption" color="warning.main">
                                                    Currently unavailable in POS; this setting will
                                                    apply if the product becomes available.
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={product.scannerAllowed}
                                                    onChange={(event) =>
                                                        updatePermission(
                                                            product.productId,
                                                            'scannerAllowed',
                                                            event.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label="Scanner"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={product.posAllowed}
                                                    disabled={product.scannerOnly}
                                                    onChange={(event) =>
                                                        updatePermission(
                                                            product.productId,
                                                            'posAllowed',
                                                            event.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label="POS"
                                        />
                                    </Box>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={saving} onClick={onClose}>
                    Cancel
                </CancelButton>
                <SubmitButton
                    loading={saving}
                    disabled={saving || noTicketsEnabled}
                    onClick={handleSave}
                >
                    Save Role
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

const TitleText = styled(Typography)(({ theme }) => ({
    paddingRight: theme.spacing(0.5),
    display: 'inline',
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
        cursor: 'pointer',
    },
}));

const MetaPill = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'pillcolor',
})(({ theme, pillcolor }) => {
    const styles = {
        green: {
            backgroundColor: '#DDF5E6',
            color: '#0F5132',
        },
        gold: {
            backgroundColor: '#FDF1CC',
            color: '#8A5A00',
        },
        gray: {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[700],
        },
    };

    return {
        height: 20,
        borderRadius: 12,
        backgroundColor: styles[pillcolor]?.backgroundColor,
        color: styles[pillcolor]?.color,
        '& .MuiChip-icon': {
            color: 'inherit',
            fontSize: 12,
        },
        '& .MuiChip-label': {
            fontSize: 12,
            fontWeight: theme.typography.fontWeightMedium,
        },
    };
});
